<template>
    <div class="col-lg-9">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="text-7">Edit Event Details</h4>
            <router-link to="/business/events" class="btn btn-link text-decoration-none">
                <i class="fas fa-arrow-left"></i> Back to Events
            </router-link>
        </div>

        <!-- Removed Success Banner for Event Details -->

        <!-- Event Details Form -->
        <div class="card card-body border-0 mb-5">
            <h6 class="font-weight-bold mb-4 border-bottom pb-2">Event Details</h6>
            <form @submit.prevent="submitEventDetails">
                <!-- Event Image Field -->
                <div class="form-group">
                    <label>Event Image</label>
                    <div v-if="!editingImage">
                        <img
                            v-if="eventForm.image && typeof eventForm.image === 'string'"
                            :src="eventForm.image"
                            alt="Event Image"
                            class="img-fluid"
                            style="max-height: 300px;"
                        />
                        <div v-else class="mb-0">
                            <em>No image uploaded</em>
                        </div>
                        <button type="button" class="btn btn-sm btn-primary mt-4 d-flex" @click="editingImage = true">
                            Change Image
                        </button>
                    </div>
                    <div v-else>
                        <file-upload
                            fileType="image"
                            :croppedImageHeight="350"
                            :aspectRatio="1"
                            v-model="eventForm.image"
                        ></file-upload>
                        <div v-text="eventForm.errors.get('image')" class="text-danger font-12"></div>
                    </div>
                </div>

                <!-- Other Event Fields -->
                <div class="form-group">
                    <label>Event Name</label>
                    <input type="text" v-model="eventForm.name" class="form-control" placeholder="Enter event name" />
                    <div v-text="eventForm.errors.get('name')" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Event URL</label>
                    <input type="text" v-model="eventForm.slug" class="form-control" placeholder="event-url-slug" />
                    <div v-text="eventForm.errors.get('slug')" class="text-danger font-12"></div>
                    <small class="text-muted" v-if="eventUrlPreview">
                        Preview: {{ eventUrlPreview }}
                    </small>
                </div>
                <div class="form-group">
                    <label>Event Type</label>
                    <select v-model="eventForm.event_type" class="form-control">
                        <option disabled value="">-- Select Event Type --</option>
                        <option value="PAID">Paid</option>
                        <option value="FREE">Free</option>
                    </select>
                    <div v-text="eventForm.errors.get('event_type')" class="text-danger font-12"></div>
                </div>

                <div class="form-group">
                    <label>Event Description</label>
                    <textarea v-model="eventForm.description" class="form-control" placeholder="Enter event description"></textarea>
                    <div v-text="eventForm.errors.get('description')" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Category</label>
                    <select v-model="eventForm.category" class="form-control">
                        <option selected value="" disabled>-- Select Category --</option>
                        <option v-for="c in eventCategories" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <div v-text="eventForm.errors.get('category')" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Venue</label>
                    <input type="text" v-model="eventForm.venue" class="form-control" placeholder="Enter venue" />
                    <div v-text="eventForm.errors.get('venue')" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Date</label>
                    <input type="date" v-model="eventForm.event_at" class="form-control" />
                    <div v-text="eventForm.errors.get('event_at')" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Time</label>
                    <input type="time" v-model="eventForm.time" class="form-control" />
                    <div v-text="eventForm.errors.get('time')" class="text-danger font-12"></div>
                </div>
                <button type="submit" class="btn btn-primary btn-block my-4" :class="{ 'btn-loading': loading }">
                    Save Event Details
                </button>
            </form>
        </div>

        <!-- Ticket Types Section -->
        <div
            class="card card-body border-0"
            v-if="eventForm.event_type === 'PAID'"
        >
            <div v-if="!eventForm.status" class="alert alert-info text-center p-3 mb-3">
                Please save your event details above before editing ticket types.
            </div>
            <div v-if="!canEditTicketTypes" class="alert alert-warning text-center p-3 mb-3">
                Ticket types editing is disabled for active events.
            </div>
            <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h6 class="font-weight-bold mb-0">Create Ticket Types</h6>
                       <button
                         class="btn btn-sm btn-primary"
                         @click="showTicketModal = true"
                         :disabled="!canEditTicketTypes"
                       >
                         Add New Ticket Type
                       </button>
                     </div>

                 <!-- ticket list (if any) -->
                 <ul
                   class="list-group list-group-flush mb-4"
                   v-if="tickets.length > 0"
                 >
                   <li
                     v-for="(ticket, index) in tickets"
                     :key="ticket.id || index"
                     class="list-group-item d-flex justify-content-between align-items-center"
                   >
                    <span>{{ ticket.name }} - ${{ ticket.price }}</span>
                     <div>
                       <i class="fas fa-pencil-alt text-secondary cursor-pointer"
                                         @click="editTicket(index)"></i>
                       <i class="fas fa-trash-alt text-danger ml-3 cursor-pointer"
                                         @click="removeTicket(index)"></i>
                     </div>
                   </li>
                 </ul>
                 <p v-else class="text-center text-muted">No tickets added yet.</p>

                <!-- only show “Save” once I actually have tickets -->
                 <button
                   v-if="tickets.length > 0"
                   class="btn btn-primary btn-block my-4"
                   @click="updateTicketTypes"
                   :class="{ 'btn-loading': loading }"
                  :disabled="!canEditTicketTypes"
                >
                  Save Ticket Types
                </button>
        </div>

            <!-- Ticket Modal -->
            <transition name="fade">
                <div
                    v-if="showTicketModal"
                    class="modal"
                    tabindex="-1"
                    style="display: block; background: rgba(0,0,0,0.5);"
                >
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">
                                    {{ isEditing ? 'Edit Ticket' : 'Add New Ticket Type' }}
                                </h5>
                                <button type="button" class="close" @click="closeTicketModal">&times;</button>
                            </div>
                            <div class="modal-body">
                                <!-- Ticket Type Selection -->
                                <div class="form-group">
                                    <label>Type Name</label>
                                    <select v-model="ticketForm.name" class="form-control">
                                        <option selected disabled>-- Select Ticket Type --</option>
                                        <option v-for="type in ticketTypes" :key="type">{{ type }}</option>
                                    </select>
                                    <div v-text="ticketForm.errors.name" class="text-danger font-12"></div>
                                </div>
                                <!-- Price (USD) -->
                                <div class="form-group">
                                    <label>Price (USD)</label>
                                    <input
                                        type="number"
                                        v-model="ticketForm.price"
                                        class="form-control"
                                        placeholder="Enter price"
                                    />
                                    <div v-text="ticketForm.errors.get('price')" class="text-danger font-12"></div>
                                </div>
                                <!-- Description -->
                                <div class="form-group">
                                    <label>Description</label>
                                    <textarea
                                        v-model="ticketForm.description"
                                        class="form-control"
                                        placeholder="Enter description"
                                    ></textarea>
                                    <div v-text="ticketForm.errors.get('description')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Ticket Type</label>
                                    <select v-model="ticketForm.type" class="form-control">
                                        <option selected value="" disabled>-- Select Type --</option>
                                        <option value="1-Day Ticket">1-Day Ticket</option>
                                        <option value="Multi-Day Ticket">Multi-Day Ticket</option>
                                    </select>
                                    <div v-text="ticketForm.errors.type" class="text-danger font-12"></div>
                                </div>
                                <!-- Number of Days (conditional) -->
                                <div v-if="ticketForm.type === 'Multi-Day Ticket'" class="form-group">
                                    <label>Number of Days</label>
                                    <input
                                        type="number"
                                        v-model="ticketForm.days"
                                        class="form-control"
                                        placeholder="Enter number of days"
                                    />
                                    <div v-text="ticketForm.errors.days" class="text-danger font-12"></div>
                                </div>
                                <!-- Quantity -->
                                <div class="form-group">
                                    <label>Quantity</label>
                                    <input
                                        type="number"
                                        v-model="ticketForm.quantity"
                                        class="form-control"
                                        placeholder="Enter quantity"
                                    />
                                    <div v-text="ticketForm.errors.get('quantity')" class="text-danger font-12"></div>
                                </div>
                                <div class="form-group">
                                    <label>Features (one per line)</label>
                                    <textarea
                                        v-model="ticketForm.features"
                                        class="form-control"
                                        placeholder="Enter features, one per line. Leave empty for default features."
                                        rows="3"
                                    ></textarea>
                                </div>

                                <div class="form-group">
                                    <label>Instructions (one per line)</label>
                                    <textarea
                                        v-model="ticketForm.instructions"
                                        class="form-control"
                                        placeholder="Enter instructions, one per line. Leave empty for default instructions."
                                        rows="3"
                                    ></textarea>
                                </div>
                                <div class="modal-footer">
                                    <button type="button" class="btn btn-sm btn-secondary" @click="closeTicketModal">
                                        Cancel
                                    </button>
                                    <button type="button" class="btn btn-sm btn-primary" @click="saveTicket">
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
</template>

<script>
import FileUpload from "../components/FileUpload.vue";
import axios from "axios";
import Form from "../../../core/forms/form";

export default {
    name: "EditEvent",
    components: { FileUpload },
    data() {
        return {
            editingImage: false, // toggles image editing mode
            eventForm: new Form({
                image: {},
                name: "",
                slug: "",
                description: "",
                category: "",
                venue: "",
                event_at: "",
                time: "",
                status: "",
                event_type: ""
            }),
            ticketForm: new Form({
                event_id: null,
                tickets: [],
                name: "",
                type: "",
                price: "",
                description: "",
                quantity: "",
                days: 0,
                features: "",
                instructions: "",

            }),
            tickets: [],
            eventCategories: [
                "Music", "Comedy", "Theater", "Film", "Sports", "Motorsport", "Workshops",
                "Conferences", "Festivals", "Exhibitions", "Community Events", "Health and Wellness",
                "Technology", "Religious/Spiritual", "Corporate", "Virtual Events", "Private Parties", "Food and Drink"
            ],
            ticketTypes: [
                "General Admission", "Standard", "VIP", "VVIP", "Early Bird", "Student", "Child",
                "Senior", "Group", "Season Pass", "Day Pass", "Weekend Pass", "Reserved Seating",
                "Standing Room", "Premium", "Backstage Access", "Meet and Greet", "Livestream Access",
                "All-Access Pass", "Corporate Package"
            ],
            showTicketModal: false,
            isEditing: false,
            editingIndex: null,
            loading: false
        };
    },
    computed: {
        canEditTicketTypes() {
            return this.eventForm.status !== 'ACTIVE';
        },
        eventUrlPreview() {
            const slug = this.eventForm.slug || '';
            if (!slug) return '';
            return `https://magetsi.co.zw/tickets/${slug}`;
        },
    },
    mounted() {
        this.fetchEvent();
    },
    methods: {
        fetchEvent() {
            const eventId = this.$route.params.id;
            this.loading = true;
            axios.get(`/business/event-onboarding/edit-events/${eventId}`)
                .then(response => {
                    const event = response.data.body?.model || response.data;
                    // after — note the added event_type
                    this.eventForm = new Form({
                        image:      event.image      || {},
                        name:       event.name       || "",
                        slug:       event.slug       || "",
                        event_type: event.event_type || "",   // ← here
                        description: event.description || "",
                        category:    event.category    || "",
                        venue:       event.venue       || "",
                        event_at:    event.event_at?.substring(0,10) || "",
                        time:        event.time        || "",
                        status:      event.status      || ""
                    });

                    // Ensure that ticket.quantity has a valid default if missing.
                    if (event.types && Array.isArray(event.types)) {
                        this.tickets = event.types.map(ticket => ({
                            id: ticket.id,
                            name: ticket.name,
                            type: ticket.type,
                            price: ticket.usd_price,
                            description: ticket.description,
                            quantity: ticket.quantity || 1,
                            days: ticket.scan_count || 0,
                            features:     ticket.features    ? JSON.parse(ticket.features).join("\n")     : "",
                            instructions: ticket.instructions? JSON.parse(ticket.instructions).join("\n") : "",

                        }));
                    } else {
                        console.warn("No ticket types found in the event data.");
                        this.tickets = [];
                    }
                })
                .catch(error => {
                    console.error("Error fetching event data:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        submitEventDetails() {
            const eventId = this.$route.params.id;
            this.loading = true;
            this.eventForm.errors.clear();

            // Create a FormData payload manually so we control which fields are sent.
            const payload = new FormData();
            payload.append("event_type", this.eventForm.event_type);
            payload.append("name", this.eventForm.name);
            if (this.eventForm.slug) {
                payload.append("slug", this.eventForm.slug);
            }
            payload.append("description", this.eventForm.description);
            payload.append("category", this.eventForm.category);
            payload.append("venue", this.eventForm.venue);
            payload.append("event_at", this.eventForm.event_at);
            payload.append("time", this.eventForm.time);

            // Only include the image field if the user is editing it.
            if (this.editingImage && this.eventForm.image) {
                payload.append("image", this.eventForm.image);
            }

            axios.post(`/business/event-onboarding/edit-events/${eventId}`, payload)
                .then(response => {
                    this.$toast.success("Event Updated Successfully!", {
                        timeout: 3000
                    });
                    this.fetchEvent();
                    setTimeout(() => {
                        this.$router.push("/business/events");
                    }, 3000);
                })
                .catch(error => {
                    console.error("Error updating event details:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        saveTicket() {
            if (this.validateTicketForm()) {
                const ticketData = {
                    id: this.ticketForm.id,
                    name: this.ticketForm.name,
                    type: this.ticketForm.type,
                    price: this.ticketForm.price,
                    description: this.ticketForm.description,
                    // Convert quantity to a number. If none is provided, default to 1.
                    quantity: this.ticketForm.quantity ? Number(this.ticketForm.quantity) : 1,
                    // For multi-day tickets, convert days to a number; otherwise, default to 0.
                    days: this.ticketForm.type === 'Multi-Day Ticket' ? Number(this.ticketForm.days) : 0,
                    features:     JSON.stringify(
                        this.ticketForm.features
                            .split("\n")
                            .map(l => l.trim())
                            .filter(l => l)
                    ),
                    instructions: JSON.stringify(
                        this.ticketForm.instructions
                            .split("\n")
                            .map(l => l.trim())
                            .filter(l => l)
                    )
                };
                if (this.isEditing) {
                    this.$set(this.tickets, this.editingIndex, ticketData);
                    this.isEditing = false;
                    this.editingIndex = null;
                } else {
                    this.tickets.push(ticketData);
                }
                this.closeTicketModal();
            }
        },
        editTicket(index) {
            const ticket = this.tickets[index];
            this.ticketForm = new Form({
                id: ticket.id,
                name: ticket.name,
                type: ticket.type,
                price: ticket.price,
                description: ticket.description,
                quantity: ticket.quantity,
                days: ticket.days || 0
            });
            this.isEditing = true;
            this.editingIndex = index;
            this.showTicketModal = true;
        },
        removeTicket(index) {
            this.tickets.splice(index, 1);
        },
        closeTicketModal() {
            this.showTicketModal = false;
            this.isEditing = false;
            this.editingIndex = null;
            // Reset ticket form fields.
            this.ticketForm.name = "";
            this.ticketForm.type = "";
            this.ticketForm.price = "";
            this.ticketForm.description = "";
            this.ticketForm.quantity = "";
            this.ticketForm.days = 0;
            this.ticketForm.features = "";
            this.ticketForm.instructions = "";
            this.ticketForm.errors.clear();
        },
        validateTicketForm() {
            let isValid = true;
            if (!this.ticketForm.name) {
                this.ticketForm.errors.set("name", "Ticket type name is required.");
                isValid = false;
            }
            if (!this.ticketForm.type) {
                this.ticketForm.errors.set("type", "Ticket type is required.");
                isValid = false;
            }
            if (!this.ticketForm.price || this.ticketForm.price <= 0) {
                this.ticketForm.errors.set("price", "Valid ticket price is required.");
                isValid = false;
            }
            if (!this.ticketForm.quantity || this.ticketForm.quantity <= 0) {
                this.ticketForm.errors.set("quantity", "Valid ticket quantity is required.");
                isValid = false;
            }
            if (!this.ticketForm.description) {
                this.ticketForm.errors.set("description", "Ticket description is required.");
                isValid = false;
            }
            if (this.ticketForm.type === 'Multi-Day Ticket' && (!this.ticketForm.days || this.ticketForm.days <= 0)) {
                this.ticketForm.errors.set("days", "Valid number of days is required.");
                isValid = false;
            }
            return isValid;
        },
        updateTicketTypes() {
            if (!this.canEditTicketTypes) {
                alert("Editing of ticket types is not allowed for active events.");
                return;
            }
            const eventId = this.$route.params.id;
            this.loading = true;
            const payload = { tickets: this.tickets };
            axios.post(`/business/event-onboarding/edit-events/${eventId}/ticket-types`, payload)
                .then(response => {
                    this.$toast.success("Ticket Types Updated Successfully!", {
                        timeout: 3000
                    });
                    setTimeout(() => {
                        this.$router.push("/business/events");
                    }, 3000);
                })
                .catch(error => {
                    console.error("Error updating ticket types:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
.cursor-pointer {
    cursor: pointer;
}
.alert {
    border-radius: 5px;
    margin-top: 20px;
}
.btn-primary {
    background-color: #252c65 !important;
    border-color: #252c65 !important;
}
.btn-primary:disabled,
.btn-primary.disabled {
    background-color: #5c5b5b !important;
    border-color: #5c5b5b !important;
    cursor: not-allowed;
    color: #fff;
}
</style>
